/**
 * b2b-quantity-picker.js
 *
 * 汎用Shopify B2B数量ピッカー（Issue #5623）。商品ハンドル・商品名による分岐を持たない。
 * 挙動は全て variant.quantity_rule / variant.quantity_price_breaks / metafield（テーブルモード上書き）
 * という「データ」だけから決まる（SPEC.md INV-1）。
 *
 * `calc`名前空間は純関数のみ（DOM非依存）。node tests/test_b2b_quantity_picker.js から
 * 直接呼び出して単体テストする。`init`はDOM描画・イベント配線を担当し、calcの結果をそのまま使う。
 *
 * Alpine・EURUSのCSS変数・グローバルストアに依存しない（素のJS）。
 */
(function (root, factory) {
  "use strict";
  var namespace = factory();
  if (typeof module !== "undefined" && module.exports) {
    module.exports = namespace;
  }
  if (typeof root !== "undefined") {
    root.B2BQuantityPicker = namespace;
  }
})(typeof window !== "undefined" ? window : undefined, function () {
  "use strict";

  // ---------------------------------------------------------------------
  // calc: 純関数群（DOM非依存・単体テスト対象）
  // ---------------------------------------------------------------------

  function toPositiveInt(value, fallback) {
    var n = parseInt(value, 10);
    if (!Number.isFinite(n) || n <= 0) return fallback;
    return n;
  }

  /**
   * variant.quantity_rule の生データ（min/max/increment。未設定はnull/undefined）を、
   * quantity_ruleが存在しない商品（min=1/increment=1/max=null）にも安全なデフォルトへ正規化する。
   */
  function normalizeQuantityRule(rawRule) {
    var raw = rawRule || {};
    var min = toPositiveInt(raw.min, 1);
    var increment = toPositiveInt(raw.increment, 1);
    var max = null;
    if (raw.max !== null && raw.max !== undefined && raw.max !== "") {
      max = toPositiveInt(raw.max, null);
    }
    if (max !== null && max < min) max = min;
    return { min: min, max: max, increment: increment };
  }

  /**
   * 自由入力欄の値をmin以上・increment境界（minからの整数倍）・max以下へ丸める。
   * quantity_rule.increment=1の商品（多くの卸商品）では実質min/maxクランプのみになる。
   */
  function roundToIncrement(qty, rawRule) {
    var rule = normalizeQuantityRule(rawRule);
    var n = parseInt(qty, 10);
    if (!Number.isFinite(n)) n = rule.min;
    if (n <= rule.min) return rule.min;
    var steps = Math.round((n - rule.min) / rule.increment);
    var rounded = rule.min + steps * rule.increment;
    if (rule.max !== null && rounded > rule.max) {
      var maxSteps = Math.floor((rule.max - rule.min) / rule.increment);
      rounded = rule.min + maxSteps * rule.increment;
    }
    return rounded;
  }

  /**
   * プリセットボタンの既定値（increment×1/2/3）。quantity_ruleが無い商品はincrement=1なので1/2/3になる。
   * max超過分は除外し、全滅した場合はminだけを返す（フェイルセーフ）。
   */
  function computePresetQuantities(rawRule) {
    var rule = normalizeQuantityRule(rawRule);
    var candidates = [
      rule.increment * 1,
      rule.increment * 2,
      rule.increment * 3,
    ];
    candidates = candidates.map(function (q) {
      return q < rule.min ? rule.min : q;
    });
    if (rule.max !== null) {
      candidates = candidates.filter(function (q) {
        return q <= rule.max;
      });
    }
    var seen = {};
    var out = [];
    candidates.forEach(function (q) {
      if (!seen[q]) {
        seen[q] = true;
        out.push(q);
      }
    });
    if (out.length === 0) out.push(rule.min);
    return out;
  }

  /**
   * quantity_price_breaks を minimumQuantity 昇順へ正規化する。不正なエントリ（数値化できない等）は除外する。
   */
  function sortPriceBreaks(rawBreaks) {
    return (rawBreaks || [])
      .map(function (b) {
        return {
          minimumQuantity: parseInt(b.minimumQuantity, 10),
          price: Number(b.price),
        };
      })
      .filter(function (b) {
        return Number.isFinite(b.minimumQuantity) && Number.isFinite(b.price);
      })
      .sort(function (a, b) {
        return a.minimumQuantity - b.minimumQuantity;
      });
  }

  /**
   * 指定数量に対して適用される単価（quantity_price_breaksのうち該当する最大のminimumQuantity）。
   * breaksが空、またはqtyが最小breakに満たない場合はbasePriceのまま。
   */
  function unitPriceForQuantity(qty, basePrice, rawBreaks) {
    var sorted = sortPriceBreaks(rawBreaks);
    var n = parseInt(qty, 10) || 0;
    var price = Number(basePrice) || 0;
    sorted.forEach(function (b) {
      if (n >= b.minimumQuantity) price = b.price;
    });
    return price;
  }

  /**
   * 早見表モード判定（INV-2）。metafield上書き（override）が tiered/flat/none のいずれかであれば最優先。
   * それ以外は quantity_price_breaks の有無だけで自動判定する（tiered=段あり / flat=段なし）。
   */
  function resolveTableMode(options) {
    var opts = options || {};
    var override = opts.override;
    if (override === "tiered" || override === "flat" || override === "none") {
      return override;
    }
    var hasBreaks = sortPriceBreaks(opts.breaks).length > 0;
    return hasBreaks ? "tiered" : "flat";
  }

  function computeRatePercent(unitPrice, retailPrice) {
    if (retailPrice === null || retailPrice === undefined || retailPrice <= 0) {
      return null;
    }
    return Math.round((unitPrice * 100) / retailPrice);
  }

  /**
   * 数量連動タイプ（tiered）の早見表行。1行目は rule.min 〜（最初のbreak手前）をbasePriceで、
   * 以降はbreakごとに1行（次のbreak手前まで。最後の行はmaxQuantity=null=上限なし）。
   * breaksが空なら空配列（INV-4: tiered早見表を生成しない）。
   */
  function buildTieredRows(options) {
    var opts = options || {};
    var basePrice = Number(opts.basePrice) || 0;
    var retailPrice =
      opts.retailPrice !== undefined && opts.retailPrice !== null
        ? Number(opts.retailPrice)
        : null;
    var showRate = opts.showRate === true;
    var rule = normalizeQuantityRule(opts.rule);
    var sorted = sortPriceBreaks(opts.breaks);

    if (sorted.length === 0) return [];

    var rows = [];
    rows.push({
      minQuantity: rule.min,
      maxQuantity: sorted[0].minimumQuantity - 1,
      unitPrice: basePrice,
      ratePercent: showRate ? computeRatePercent(basePrice, retailPrice) : null,
    });
    sorted.forEach(function (b, idx) {
      var next = sorted[idx + 1];
      rows.push({
        minQuantity: b.minimumQuantity,
        maxQuantity: next ? next.minimumQuantity - 1 : null,
        unitPrice: b.price,
        ratePercent: showRate ? computeRatePercent(b.price, retailPrice) : null,
      });
    });
    return rows;
  }

  /**
   * 固定タイプ（flat）の早見表行。単価は数量によらず一定（basePrice）。まとめ買い数量ごとの合計を静的に見せる。
   * quantitiesを渡さない場合はプリセット数量（increment×1/2/3）を使う。
   */
  function buildFlatRows(options) {
    var opts = options || {};
    var basePrice = Number(opts.basePrice) || 0;
    var retailPrice =
      opts.retailPrice !== undefined && opts.retailPrice !== null
        ? Number(opts.retailPrice)
        : null;
    var showRate = opts.showRate === true;
    var rule = normalizeQuantityRule(opts.rule);
    var quantities =
      opts.quantities && opts.quantities.length
        ? opts.quantities
        : computePresetQuantities(rule);

    var seen = {};
    var uniq = quantities.filter(function (q) {
      if (seen[q]) return false;
      seen[q] = true;
      return true;
    });

    return uniq.map(function (q) {
      return {
        quantity: q,
        unitPrice: basePrice,
        subtotal: basePrice * q,
        ratePercent: showRate
          ? computeRatePercent(basePrice, retailPrice)
          : null,
      };
    });
  }

  /**
   * カート投入直前の合計表示に使う要約（数量・適用単価・小計）。
   */
  function computeCartSummary(qty, basePrice, rawBreaks) {
    var n = parseInt(qty, 10) || 0;
    var unitPrice = unitPriceForQuantity(n, basePrice, rawBreaks);
    return {
      quantity: n,
      unitPrice: unitPrice,
      subtotal: unitPrice * n,
    };
  }

  var calc = {
    normalizeQuantityRule: normalizeQuantityRule,
    roundToIncrement: roundToIncrement,
    computePresetQuantities: computePresetQuantities,
    sortPriceBreaks: sortPriceBreaks,
    unitPriceForQuantity: unitPriceForQuantity,
    resolveTableMode: resolveTableMode,
    buildTieredRows: buildTieredRows,
    buildFlatRows: buildFlatRows,
    computeCartSummary: computeCartSummary,
  };

  // ---------------------------------------------------------------------
  // UI: DOM描画・イベント配線（calcの結果をそのまま使う。分岐ロジックは持たない）
  // ---------------------------------------------------------------------

  function formatMoney(cents, moneyFormat) {
    var amount = (Number(cents) || 0) / 100;
    var formatted = amount.toLocaleString("ja-JP", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
    if (moneyFormat && moneyFormat.indexOf("{{amount}}") !== -1) {
      return moneyFormat.replace("{{amount}}", formatted);
    }
    if (moneyFormat && moneyFormat.indexOf("{{ amount }}") !== -1) {
      return moneyFormat.replace("{{ amount }}", formatted);
    }
    return "¥" + formatted;
  }

  function safeJsonParse(str, fallback) {
    if (!str) return fallback;
    try {
      return JSON.parse(str);
    } catch (e) {
      return fallback;
    }
  }

  function readState(rootEl) {
    var ds = rootEl.dataset;
    var rule = normalizeQuantityRule(safeJsonParse(ds.rule, {}));
    var breaks = safeJsonParse(ds.breaks, []);
    var basePrice = Number(ds.basePrice) || 0;
    var retailPrice = ds.retailPrice ? Number(ds.retailPrice) : null;
    var override = ds.tableModeOverride || "";
    var showRate = ds.showRate === "true";
    var moneyFormat = ds.moneyFormat || "{{amount}}";
    var mode = resolveTableMode({ override: override, breaks: breaks });
    return {
      rule: rule,
      breaks: breaks,
      basePrice: basePrice,
      retailPrice: retailPrice,
      showRate: showRate,
      moneyFormat: moneyFormat,
      mode: mode,
    };
  }

  function findQuantityInput(rootEl, state) {
    var selector = rootEl.dataset.quantityInputSelector;
    if (!selector) return null;
    var selectors = selector.split(",");
    for (var i = 0; i < selectors.length; i++) {
      var el = document.querySelector(selectors[i].trim());
      if (el) return el;
    }
    return null;
  }

  function setQuantity(rootEl, state, refs, qty) {
    var rounded = roundToIncrement(qty, state.rule);
    refs.freeInput.value = rounded;
    var summary = computeCartSummary(rounded, state.basePrice, state.breaks);

    if (refs.quantityInput) {
      refs.quantityInput.value = rounded;
      refs.quantityInput.dispatchEvent(new Event("change", { bubbles: true }));
    }

    Array.prototype.forEach.call(refs.presetButtons, function (btn) {
      btn.classList.toggle(
        "is-active",
        parseInt(btn.dataset.qty, 10) === rounded,
      );
    });
    Array.prototype.forEach.call(refs.tableRows, function (row) {
      var min = parseInt(row.dataset.minQty, 10);
      var maxAttr = row.dataset.maxQty;
      var max = maxAttr === "" ? null : parseInt(maxAttr, 10);
      var isActive = rounded >= min && (max === null || rounded <= max);
      row.classList.toggle("is-active", isActive);
    });

    if (refs.summaryEl) {
      refs.summaryEl.textContent =
        summary.quantity +
        " x " +
        formatMoney(summary.unitPrice, state.moneyFormat) +
        " = " +
        formatMoney(summary.subtotal, state.moneyFormat);
    }

    rootEl.dispatchEvent(
      new CustomEvent("b2b-quantity-picker:change", {
        bubbles: true,
        detail: summary,
      }),
    );
  }

  function renderPresets(rootEl, state) {
    var container = rootEl.querySelector("[data-b2bqp-presets]");
    if (!container) return [];
    container.innerHTML = "";
    var presets = computePresetQuantities(state.rule);
    return presets.map(function (qty) {
      var btn = document.createElement("button");
      btn.type = "button";
      btn.className = "b2bqp__preset-btn";
      btn.dataset.qty = String(qty);
      btn.textContent = String(qty);
      container.appendChild(btn);
      return btn;
    });
  }

  function renderTable(rootEl, state) {
    var container = rootEl.querySelector("[data-b2bqp-table]");
    if (!container) return [];
    container.innerHTML = "";
    if (state.mode === "none") return [];

    var rows =
      state.mode === "tiered"
        ? buildTieredRows({
            basePrice: state.basePrice,
            breaks: state.breaks,
            retailPrice: state.retailPrice,
            showRate: state.showRate,
            rule: state.rule,
          })
        : buildFlatRows({
            basePrice: state.basePrice,
            rule: state.rule,
            retailPrice: state.retailPrice,
            showRate: state.showRate,
          });

    if (rows.length === 0) return [];

    var table = document.createElement("table");
    table.className = "b2bqp__table-el";
    var tbody = document.createElement("tbody");

    var rowEls = rows.map(function (row) {
      var tr = document.createElement("tr");
      tr.className = "b2bqp__table-row";
      var qty = row.quantity !== undefined ? row.quantity : row.minQuantity;
      var maxQty =
        row.maxQuantity !== undefined ? row.maxQuantity : row.quantity;
      tr.dataset.minQty = String(
        row.minQuantity !== undefined ? row.minQuantity : row.quantity,
      );
      tr.dataset.maxQty =
        maxQty === null || maxQty === undefined ? "" : String(maxQty);
      tr.dataset.qty = String(qty);

      var qtyLabel =
        row.minQuantity !== undefined
          ? row.maxQuantity !== null && row.maxQuantity !== undefined
            ? row.minQuantity + "〜" + row.maxQuantity
            : row.minQuantity + "〜"
          : String(row.quantity);

      var td1 = document.createElement("td");
      td1.textContent = qtyLabel;
      tr.appendChild(td1);

      var td2 = document.createElement("td");
      td2.textContent = formatMoney(row.unitPrice, state.moneyFormat);
      tr.appendChild(td2);

      if (row.subtotal !== undefined) {
        var td3 = document.createElement("td");
        td3.textContent = formatMoney(row.subtotal, state.moneyFormat);
        tr.appendChild(td3);
      }

      if (
        state.showRate &&
        row.ratePercent !== null &&
        row.ratePercent !== undefined
      ) {
        var td4 = document.createElement("td");
        td4.textContent = row.ratePercent + "%";
        tr.appendChild(td4);
      }

      tbody.appendChild(tr);
      return tr;
    });

    table.appendChild(tbody);
    container.appendChild(table);
    return rowEls;
  }

  function init(rootEl) {
    if (!rootEl || rootEl.dataset.b2bqpInitialized === "true") return;
    rootEl.dataset.b2bqpInitialized = "true";

    var state = readState(rootEl);
    var freeInput = rootEl.querySelector("[data-b2bqp-free-input]");
    if (!freeInput) return;

    var refs = {
      freeInput: freeInput,
      quantityInput: findQuantityInput(rootEl, state),
      summaryEl: rootEl.querySelector("[data-b2bqp-summary]"),
      presetButtons: renderPresets(rootEl, state),
      tableRows: renderTable(rootEl, state),
    };

    refs.presetButtons.forEach(function (btn) {
      btn.addEventListener("click", function () {
        setQuantity(rootEl, state, refs, parseInt(btn.dataset.qty, 10));
      });
    });

    refs.tableRows.forEach(function (row) {
      row.addEventListener("click", function () {
        setQuantity(rootEl, state, refs, parseInt(row.dataset.qty, 10));
      });
    });

    freeInput.addEventListener("change", function () {
      setQuantity(rootEl, state, refs, freeInput.value);
    });

    setQuantity(rootEl, state, refs, freeInput.value || state.rule.min);
  }

  if (typeof document !== "undefined") {
    document.addEventListener("DOMContentLoaded", function () {
      var roots = document.querySelectorAll("[data-b2b-quantity-picker]");
      Array.prototype.forEach.call(roots, init);
    });
  }

  return { calc: calc, init: init };
});
